import { EyeSlashIcon, LockClosedIcon, ShieldCheckIcon, UserIcon } from '@heroicons/react/24/outline'
import { ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router'

import Button from '@/components/Button'

export default function PrivacyPolicy() {
    const navigate = useNavigate()
    return (
        <div className='min-h-dvh bg-gradient-to-br from-cyan-400 via-cyan-500 to-gray-200'>
            <div className='absolute inset-0 overflow-hidden'>
                <div className='absolute animate-pulse top-10 right-8 w-20 h-20 sm:w-32 sm:h-32 bg-white/10 rounded-2xl rotate-12'></div>
                <div className='absolute animate-pulse bottom-20 left-8 w-24 h-24 bg-white/15 rounded-2xl -rotate-6'></div>
                <div className='absolute animate-pulse top-1/2 right-12 w-16 h-16 bg-yellow-200/25 rounded-xl rotate-45'></div>
            </div>

            <div className='relative z-10 max-w-4xl mx-auto px-4 py-8 sm:py-12'>
                <div className='text-center mb-8 sm:mb-12'>
                    <div className='flex justify-center mb-4'>
                        <ShieldCheckIcon className='w-16 h-16 text-white' />
                    </div>
                    <h1 className='text-3xl sm:text-4xl font-bold text-white mb-4'>Privacy Policy</h1>
                    <p className='text-white/90 text-lg'>Effective Date: May 25, 2026</p>
                    <p className='text-white/80 mt-2 max-w-2xl mx-auto'>
                        BrightPath is committed to protecting your privacy. This policy explains how we collect, use,
                        and safeguard your personal information.
                    </p>
                </div>

                <div className='grid grid-cols-1 md:grid-cols-3 gap-4 mb-8'>
                    <div className='bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center'>
                        <EyeSlashIcon className='w-8 h-8 text-white mx-auto mb-2' />
                        <p className='text-white font-medium text-sm'>No Data Training</p>
                        <p className='text-white/80 text-xs mt-1'>Your data never trains AI models</p>
                    </div>
                    <div className='bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center'>
                        <LockClosedIcon className='w-8 h-8 text-white mx-auto mb-2' />
                        <p className='text-white font-medium text-sm'>Secure & Private</p>
                        <p className='text-white/80 text-xs mt-1'>Industry-standard encryption</p>
                    </div>
                    <div className='bg-white/20 backdrop-blur-sm rounded-xl p-4 text-center'>
                        <UserIcon className='w-8 h-8 text-white mx-auto mb-2' />
                        <p className='text-white font-medium text-sm'>Your Control</p>
                        <p className='text-white/80 text-xs mt-1'>Update or delete data anytime</p>
                    </div>
                </div>

                <div className='bg-white/95 backdrop-blur-sm rounded-2xl shadow-xl p-6 sm:p-8 lg:p-10'>
                    <div className='prose prose-gray max-w-none'>
                        <div className='mb-8 p-4 bg-cyan-50 rounded-xl border-l-4 border-cyan-400'>
                            <p className='text-gray-700 leading-relaxed'>
                                Welcome to BrightPath — a digital platform developed by BrightPath Inc. ("BrightPath,"
                                "we," "our," or "us"). We are committed to protecting your privacy and ensuring that
                                your personal information is handled responsibly. This Privacy Policy explains how we
                                collect, use, and safeguard information when you use the BrightPath web or mobile app
                                (the "Platform").
                            </p>
                        </div>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    1
                                </span>
                                Information We Collect
                            </h2>
                            <p className='text-gray-700 mb-4'>
                                BrightPath collects health-related information about your child only to personalize
                                your experience inside the Platform. We are not a healthcare provider, we do not
                                maintain medical records, and we do not use this information to make clinical decisions
                                or to deliver treatment.
                            </p>
                            <p className='text-gray-700 mb-4'>
                                To provide a personalized experience and meaningful guidance, BrightPath may collect the
                                following types of information:
                            </p>

                            <h3 className='text-lg font-semibold text-gray-800 mb-2'>a. User-Provided Information</h3>

                            <div className='space-y-4 mb-4'>
                                <div>
                                    <p className='font-medium text-gray-800 mb-1'>Account Information</p>
                                    <p className='text-gray-700 ml-4'>
                                        Email address (required for account creation and login)
                                    </p>
                                </div>
                                <div>
                                    <p className='font-medium text-gray-800 mb-1'>About You</p>
                                    <p className='text-gray-700 ml-4'>
                                        Guardian nickname (optional, for personalization), guardian role (e.g., Parent,
                                        Grandparent, Foster parent)
                                    </p>
                                </div>
                                <div>
                                    <p className='font-medium text-gray-800 mb-1'>About Your Child</p>
                                    <p className='text-gray-700 ml-4'>
                                        Child's nickname (optional, for personalization), child's birth month and year
                                        (or, for legacy accounts, age range), child's gender (optional), diagnosis
                                        status and developmental stage, communication level, support needs, main areas
                                        of concern (e.g., speech delay, sensory, behavioral, or learning issues),
                                        evaluation waitlist signup date (collected conditionally when applicable),
                                        services your child currently receives (collected only with your explicit
                                        opt-in consent; categories such as Early Intervention, ABA, Speech, Occupational
                                        Therapy, Physical Therapy, or Other), goals for child and caregiver
                                    </p>
                                </div>
                                <div>
                                    <p className='font-medium text-gray-800 mb-1'>Optional</p>
                                    <p className='text-gray-700 ml-4'>Profile photo</p>
                                </div>
                            </div>

                            <h3 className='text-lg font-semibold text-gray-800 mb-2'>
                                b. Automatically Collected Information
                            </h3>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Usage data (e.g., app interactions, feature usage, frequency of check-ins)
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Device type, browser type, and IP address (for performance and security monitoring)
                                </li>
                            </ul>

                            <h3 className='text-lg font-semibold text-gray-800 mb-2'>c. What We Don't Collect</h3>
                            <div className='bg-green-50 border border-green-200 rounded-lg p-4'>
                                <ul className='space-y-2 text-gray-700'>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        We do not require legal names or physical addresses. We collect optional
                                        nicknames for personalization only.
                                    </li>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        We do not collect or store government-issued identifiers.
                                    </li>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        We do not request or store credit card information unless processed securely
                                        through third-party payment services (e.g., Stripe).
                                    </li>
                                    <li className='flex items-start'>
                                        <span className='text-cyan-500 mr-2'>•</span>
                                        When you tell us about services your child currently receives, we collect only
                                        the type of service (e.g., Speech, OT). We do not collect provider names, clinic
                                        names, therapist names, dosage, frequency, session notes, or medical records.
                                    </li>
                                </ul>
                            </div>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    2
                                </span>
                                How We Use Your Information
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                We use your information solely to support and enhance your BrightPath experience:
                            </p>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Personalize your care roadmap, checklist, and daily reminders
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Provide AI-generated guidance and insights based on your inputs
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Improve app functionality, navigation, and accessibility
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Enable you to save chat history, checklists, and progress within your account
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Sync your data securely across your devices when logged into your account
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Health-related information about your child is used solely inside the Platform to
                                    personalize your roadmap and AI-assisted guidance. It is never used for clinical
                                    decision-making, healthcare delivery, profiling for marketing, or any purpose
                                    outside of personalizing your BrightPath experience.
                                </li>
                            </ul>
                            <div className='bg-red-50 border border-red-200 rounded-lg p-4'>
                                <p className='text-gray-700 font-medium'>
                                    We do not sell, rent, or share your personal information for marketing or
                                    advertising purposes.
                                </p>
                            </div>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    3
                                </span>
                                AI Features and Your Data
                            </h2>
                            <p className='text-gray-700 mb-4'>
                                BrightPath uses AI to deliver empathetic, evidence-informed support. To ensure
                                transparency and trust:
                            </p>
                            <div className='bg-purple-50 border-2 border-purple-200 rounded-xl p-6 space-y-4'>
                                <div className='flex items-start'>
                                    <div className='bg-purple-100 rounded-full p-2 mr-3 mt-1'>
                                        <ShieldCheckIcon className='w-5 h-5 text-purple-600' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800 mb-1'>No AI Training</p>
                                        <p className='text-gray-700'>
                                            Your data is never used to train or fine-tune any AI model.
                                        </p>
                                    </div>
                                </div>
                                <div className='flex items-start'>
                                    <div className='bg-purple-100 rounded-full p-2 mr-3 mt-1'>
                                        <LockClosedIcon className='w-5 h-5 text-purple-600' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800 mb-1'>Pre-trained Models</p>
                                        <p className='text-gray-700'>
                                            Our AI systems are pre-trained and generate responses based on your profile
                                            information and inputs.
                                        </p>
                                    </div>
                                </div>
                                <div className='flex items-start'>
                                    <div className='bg-purple-100 rounded-full p-2 mr-3 mt-1'>
                                        <EyeSlashIcon className='w-5 h-5 text-purple-600' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800 mb-1'>Secure Storage</p>
                                        <p className='text-gray-700'>
                                            Chat and interaction history are stored securely within your account to
                                            maintain continuity between sessions.
                                        </p>
                                    </div>
                                </div>
                                <div className='flex items-start'>
                                    <div className='bg-purple-100 rounded-full p-2 mr-3 mt-1'>
                                        <ShieldCheckIcon className='w-5 h-5 text-purple-600' />
                                    </div>
                                    <div>
                                        <p className='font-semibold text-gray-800 mb-1'>No Vendor Sharing</p>
                                        <p className='text-gray-700'>
                                            Your data is not shared with AI vendors for model improvement or analytics.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    4
                                </span>
                                Data Security
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                We apply industry-standard security protocols to protect your information:
                            </p>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Encryption of all data in transit and at rest
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Regular vulnerability assessments and security audits
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Restricted internal access to sensitive data
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Secure authentication and account protection practices
                                </li>
                            </ul>
                            <p className='text-gray-700 leading-relaxed'>
                                While we take strong precautions, no system can guarantee absolute security. Users are
                                encouraged to keep login credentials private and secure.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    5
                                </span>
                                Third-Party Services
                            </h2>
                            <p className='text-gray-700 mb-3'>
                                To provide platform functionality, BrightPath may use third-party vendors such as:
                            </p>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Cloud hosting providers (for secure storage and uptime)
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    AI API services (for language processing and insights)
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Payment processors (for subscription and billing management)
                                </li>
                            </ul>

                            <p className='text-gray-700 mb-2 font-medium'>Where applicable:</p>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    All vendors are bound by confidentiality and data protection agreements.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    We disable data logging and sharing wherever possible.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    We do not permit vendors to use your data for model training or independent
                                    analytics.
                                </li>
                            </ul>

                            <h3 className='text-lg font-semibold text-gray-800 mb-2'>
                                Analytics and Performance Tools
                            </h3>
                            <p className='text-gray-700 mb-2'>
                                BrightPath may use analytics tools to understand aggregate app usage and improve the
                                user experience.
                            </p>
                            <ul className='space-y-2 text-gray-700'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    These tools collect non-identifiable information such as page views, session
                                    duration, and navigation patterns.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    A pseudonymous user identifier may be sent to analytics services for usage
                                    analysis; this identifier cannot be used to directly identify you.
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    We do not use behavioral advertising, cross-site tracking, or targeted marketing
                                    analytics.
                                </li>
                            </ul>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    6
                                </span>
                                Cookies
                            </h2>
                            <p className='text-gray-700'>
                                We use one secure, HTTP-only cookie to maintain your login session. This cookie is used
                                solely for authentication and is deleted when you sign out. We do not use third-party
                                tracking cookies.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    6a
                                </span>
                                Consumer Health Data
                            </h2>
                            <p className='text-gray-700 mb-4'>
                                Some of the information you share with BrightPath about your child (for example,
                                diagnosis status, developmental concerns, communication level, support needs, and
                                services your child currently receives) qualifies as "consumer health data" under
                                Washington's My Health My Data Act and similar state laws.
                            </p>
                            <div className='space-y-4'>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>
                                        Categories of consumer health data we collect.
                                    </p>
                                    <p className='text-gray-700'>
                                        Diagnosis status and developmental stage, areas of concern, communication
                                        level, support needs, child's birth month and year, and services your child
                                        currently receives.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>Source of the data.</p>
                                    <p className='text-gray-700'>
                                        You. We collect consumer health data only from the information you choose to
                                        enter into the Platform during onboarding or profile updates.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>How we use it.</p>
                                    <p className='text-gray-700'>
                                        Solely to personalize your care roadmap, daily tips, recommended resources, and
                                        AI-assisted guidance inside the Platform. We do not use consumer health data
                                        for marketing, advertising, profiling, or any purpose unrelated to your use of
                                        BrightPath.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>Sharing.</p>
                                    <p className='text-gray-700'>
                                        We do not sell consumer health data. We do not share it with third parties for
                                        marketing or advertising. We share it only with infrastructure vendors (such as
                                        our cloud hosting and AI API providers) acting on our behalf and bound by
                                        confidentiality and data-protection terms.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>Your consent.</p>
                                    <p className='text-gray-700'>
                                        Before BrightPath collects information about services your child currently
                                        receives, we ask for your explicit, opt-in consent. You may decline; the rest
                                        of the Platform remains fully available to you. You may withdraw your consent
                                        at any time through your profile, after which the field is cleared.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>Your rights.</p>
                                    <p className='text-gray-700'>
                                        You may request access to, a copy of, or deletion of your consumer health data
                                        at any time by contacting{' '}
                                        <a
                                            href='mailto:hello@brightpathforautism.com'
                                            className='text-cyan-600 hover:text-cyan-700 underline font-medium'
                                        >
                                            hello@brightpathforautism.com
                                        </a>
                                        . You may also withdraw any consent you previously gave. Requests are processed
                                        without charge and without retaliation.
                                    </p>
                                </div>
                                <div>
                                    <p className='font-semibold text-gray-800 mb-1'>Washington residents.</p>
                                    <p className='text-gray-700'>
                                        The Washington Attorney General enforces the My Health My Data Act and accepts
                                        complaints regarding consumer health data practices.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    7
                                </span>
                                Your Rights and Choices
                            </h2>
                            <p className='text-gray-700 mb-3'>You have full control over your information:</p>
                            <ul className='space-y-2 text-gray-700 mb-4'>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    Access or update your data anytime through your profile
                                </li>
                                <li className='flex items-start'>
                                    <span className='text-cyan-500 mr-2'>•</span>
                                    <span className='flex-1'>
                                        Delete your account and data by request at any time by contacting{' '}
                                        <a
                                            href='mailto:hello@brightpathforautism.com'
                                            className='text-cyan-600 hover:text-cyan-700 underline break-words font-medium'
                                        >
                                            hello@brightpathforautism.com
                                        </a>
                                    </span>
                                </li>
                            </ul>
                            <p className='text-gray-700'>
                                To delete your account, contact hello@brightpathforautism.com. Upon processing your
                                request, data is removed from active databases immediately. Backup copies are purged
                                within 30 days.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    7a
                                </span>
                                Communications and Notifications
                            </h2>
                            <p className='text-gray-700 mb-2'>
                                We may send you essential service communications such as feature updates, security
                                alerts, or account notices.
                            </p>
                            <p className='text-gray-700'>
                                You may manage or opt out of non-essential communications (such as newsletters or
                                product announcements) at any time by contacting us.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    8
                                </span>
                                Children's Privacy
                            </h2>
                            <p className='text-gray-700 mb-2'>
                                BrightPath is designed for parents and caregivers managing autism care for children or
                                dependents.
                            </p>
                            <p className='text-gray-700'>
                                We do not knowingly collect personal information directly from children under 13 years
                                of age. If we become aware that such data has been inadvertently collected, we will
                                promptly delete it.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    9
                                </span>
                                Retention of Information
                            </h2>
                            <p className='text-gray-700 mb-2'>
                                We retain data only as long as necessary to fulfill the purposes described in this
                                policy or as required by law.
                            </p>
                            <p className='text-gray-700'>
                                When you delete your account, your personal data and chat history are removed from
                                active databases immediately. Backup copies are purged within 30 days.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    10
                                </span>
                                Changes to This Policy
                            </h2>
                            <p className='text-gray-700 mb-2'>
                                We may periodically update this Privacy Policy to reflect new features or legal
                                requirements.
                            </p>
                            <p className='text-gray-700'>
                                When changes occur, we will notify users via the app or email. Continued use of
                                BrightPath after notice constitutes acceptance of the updated terms.
                            </p>
                        </section>

                        <section className='mb-8'>
                            <h2 className='text-2xl font-bold text-gray-800 mb-4 flex items-center'>
                                <span className='bg-cyan-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3'>
                                    11
                                </span>
                                Contact Us
                            </h2>
                            <p className='text-gray-700 mb-4'>
                                If you have any questions, feedback, or concerns regarding privacy or data security,
                                please contact us:
                            </p>
                            <div className='bg-cyan-50 border border-cyan-200 rounded-lg p-6'>
                                <div className='text-center'>
                                    <p className='text-gray-700 text-lg font-semibold mb-2'>BrightPath Inc.</p>
                                    <p className='text-gray-600 mb-1'>
                                        Email:{' '}
                                        <a
                                            href='mailto:hello@brightpathforautism.com'
                                            className='text-cyan-600 hover:text-cyan-700 underline font-medium'
                                        >
                                            hello@brightpathforautism.com
                                        </a>
                                    </p>
                                    <p className='text-gray-600'>
                                        Official Site:{' '}
                                        <a
                                            href='https://brightpathforautism.com'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            className='text-cyan-600 hover:text-cyan-700 underline font-medium'
                                        >
                                            https://brightpathforautism.com
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </section>
                    </div>
                </div>
                <div className='flex  mt-5 w-full justify-center'>
                    <Button
                        onClick={() => navigate(-1)}
                        className='inline-flex items-center px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-md hover:bg-brand-700 transition-colors'
                    >
                        <ArrowLeft className='w-4 h-4 ml-2' />
                        Go Back
                    </Button>
                </div>
                <div className='text-center mt-8'>
                    <p className='text-white/80 text-sm'>
                        Your privacy and trust are fundamental to our mission of supporting families.
                    </p>
                </div>
            </div>
        </div>
    )
}
